import { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger,
} from "./ui/sheet";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import {
  StickyNote, Plus, Pin, Trash2, Link2, Search, Loader2, Pencil, X, FileText, User,
} from "lucide-react";

const COLORS = {
  amber: { hex: "#f59e0b", dot: "bg-amber-500" },
  sky: { hex: "#0ea5e9", dot: "bg-sky-500" },
  emerald: { hex: "#10b981", dot: "bg-emerald-500" },
  rose: { hex: "#f43f5e", dot: "bg-rose-500" },
  violet: { hex: "#8b5cf6", dot: "bg-violet-500" },
  slate: { hex: "#64748b", dot: "bg-slate-400" },
};
const COLOR_KEYS = Object.keys(COLORS);
const EMPTY = { titolo: "", contenuto: "", colore: "amber", fissato: false, collegato_tipo: "", collegato_id: "", collegato_label: "" };

function CollegatoSelect({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [opts, setOpts] = useState([]);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const tmr = useRef(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    clearTimeout(tmr.current);
    tmr.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/appunti/collegabili", { params: { q } });
        setOpts(data || []);
      } catch { setOpts([]); }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(tmr.current);
  }, [q, open]);

  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  return (
    <div className="relative" ref={boxRef}>
      {value?.collegato_id ? (
        <div className="flex items-center justify-between gap-2 h-10 px-3 rounded-md bg-slate-900/60 border border-slate-700" data-testid="appunto-collegato-chip">
          <span className="flex items-center gap-2 min-w-0 text-sm text-slate-200">
            {value.collegato_tipo === "pratica" ? <FileText className="h-4 w-4 text-amber-400 shrink-0" /> : <User className="h-4 w-4 text-sky-400 shrink-0" />}
            <span className="truncate">{value.collegato_label}</span>
          </span>
          <button type="button" onClick={() => onChange({ collegato_tipo: "", collegato_id: "", collegato_label: "" })} data-testid="appunto-collegato-clear" className="text-slate-400 hover:text-rose-400 shrink-0"><X className="h-4 w-4" /></button>
        </div>
      ) : (
        <button type="button" onClick={() => setOpen((o) => !o)} data-testid="appunto-collegato-trigger" className="w-full h-10 px-3 rounded-md bg-slate-900/60 border border-slate-700 text-sm text-slate-400 flex items-center gap-2 hover:border-slate-600">
          <Link2 className="h-4 w-4" /> Collega a una pratica o soggetto…
        </button>
      )}
      {open && !value?.collegato_id && (
        <div className="absolute z-50 left-0 right-0 mt-1 rounded-md border border-slate-700 bg-[#0e1526] shadow-xl overflow-hidden">
          <div className="relative p-2 border-b border-slate-800">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca pratica o soggetto…" data-testid="appunto-collegato-search" className="pl-9 h-9 bg-slate-900/60 border-slate-700" />
          </div>
          <div className="max-h-56 overflow-y-auto">
            {loading ? (
              <div className="p-4 flex justify-center"><Loader2 className="h-4 w-4 animate-spin text-amber-500" /></div>
            ) : opts.length === 0 ? (
              <div className="p-4 text-center text-xs text-slate-500">Nessun risultato</div>
            ) : opts.map((o) => (
              <button key={`${o.tipo}-${o.id}`} type="button" data-testid="appunto-collegato-opt"
                onClick={() => { onChange({ collegato_tipo: o.tipo, collegato_id: o.id, collegato_label: o.label }); setOpen(false); setQ(""); }}
                className="w-full text-left px-3 py-2 flex items-center gap-2 text-sm text-slate-200 hover:bg-slate-800/60">
                {o.tipo === "pratica" ? <FileText className="h-4 w-4 text-amber-400 shrink-0" /> : <User className="h-4 w-4 text-sky-400 shrink-0" />}
                <span className="truncate">{o.label}</span>
                <span className="ml-auto text-[10px] uppercase text-slate-500">{o.tipo}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AppuntoDialog({ open, onOpenChange, initial, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setF(initial ? { ...EMPTY, ...initial } : EMPTY); }, [open, initial]);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const save = async () => {
    if (!f.contenuto.trim()) { toast.error("Il contenuto è obbligatorio"); return; }
    setSaving(true);
    try {
      const payload = {
        titolo: f.titolo, contenuto: f.contenuto, colore: f.colore, fissato: !!f.fissato,
        collegato_tipo: f.collegato_tipo, collegato_id: f.collegato_id, collegato_label: f.collegato_label,
      };
      if (initial?.id) await api.patch(`/appunti/${initial.id}`, payload);
      else await api.post("/appunti", payload);
      toast.success("Appunto salvato");
      onSaved();
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121824] border-slate-700 text-slate-100 max-w-lg" data-testid="appunto-dialog">
        <DialogHeader><DialogTitle className="font-head flex items-center gap-2"><StickyNote className="h-5 w-5 text-amber-400" /> {initial?.id ? "Modifica appunto" : "Nuovo appunto"}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-1">
          <div>
            <Label className="text-slate-300 text-sm">Titolo <span className="text-slate-500">(facoltativo)</span></Label>
            <Input value={f.titolo} onChange={(e) => set("titolo", e.target.value)} placeholder="Titolo dell'appunto" data-testid="appunto-titolo" className="mt-1.5 bg-slate-900/60 border-slate-700" />
          </div>
          <div>
            <Label className="text-slate-300 text-sm">Contenuto <span className="text-rose-400">*</span></Label>
            <Textarea value={f.contenuto} onChange={(e) => set("contenuto", e.target.value)} placeholder="Scrivi qui il tuo appunto…" rows={4} data-testid="appunto-contenuto" className="mt-1.5 bg-slate-900/60 border-slate-700 resize-none" />
          </div>
          <div>
            <Label className="text-slate-300 text-sm">Colore</Label>
            <div className="flex items-center gap-2 mt-2">
              {COLOR_KEYS.map((c) => (
                <button key={c} type="button" onClick={() => set("colore", c)} data-testid={`appunto-colore-${c}`}
                  className={`h-7 w-7 rounded-full ${COLORS[c].dot} transition-transform ${f.colore === c ? "ring-2 ring-offset-2 ring-offset-[#121824] ring-white scale-110" : "opacity-70 hover:opacity-100"}`} aria-label={c} />
              ))}
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox checked={!!f.fissato} onCheckedChange={(v) => set("fissato", !!v)} data-testid="appunto-fissato" />
            <span className="text-sm text-slate-200 flex items-center gap-1.5"><Pin className="h-3.5 w-3.5 text-amber-400" /> Fissa in alto</span>
          </label>
          <div>
            <Label className="text-slate-300 text-sm">Collegato a <span className="text-slate-500">(facoltativo)</span></Label>
            <div className="mt-1.5">
              <CollegatoSelect value={f} onChange={(patch) => setF((s) => ({ ...s, ...patch }))} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="appunto-annulla" className="border-slate-700 text-slate-200 hover:bg-slate-800">Annulla</Button>
          <Button type="button" onClick={save} disabled={saving} data-testid="appunto-salva" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
            {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null} Salva appunto
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AppuntiRapidiButton() {
  const light = true;
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/appunti"); setItems(data || []); setCount((data || []).length); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);

  // Carica il conteggio al mount e aggiorna quando il pannello si apre.
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const togglePin = async (a) => {
    try {
      await api.patch(`/appunti/${a.id}`, { ...a, fissato: !a.fissato });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (a) => {
    try { await api.delete(`/appunti/${a.id}`); load(); toast.success("Appunto eliminato"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <button data-testid="appunti-rapidi-trigger" title="Appunti rapidi" aria-label="Appunti rapidi"
            className="relative h-9 w-9 flex items-center justify-center rounded-lg text-slate-400 hover:text-amber-400 hover:bg-slate-800/60 transition-colors">
            <StickyNote className="h-5 w-5" />
            {count > 0 && (
              <span data-testid="appunti-badge-count"
                style={{ boxShadow: `0 0 0 2px ${light ? "#eef1f5" : "#0b1120"}` }}
                className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-amber-500 text-[#0a0d14] text-[10px] font-bold leading-none">
                {count > 99 ? "99+" : count}
              </span>
            )}
          </button>
        </SheetTrigger>
        <SheetContent side="right" style={{ background: light ? "#eef1f5" : "#0b1120" }} className="border-slate-800 text-slate-100 w-full sm:max-w-md p-0 flex flex-col" data-testid="appunti-rapidi-panel">
          <SheetHeader className="px-5 py-4 border-b border-slate-800">
            <SheetTitle className="font-head flex items-center gap-2 text-slate-100"><StickyNote className="h-5 w-5 text-amber-400" /> Appunti rapidi</SheetTitle>
          </SheetHeader>
          <div className="px-5 py-3 border-b border-slate-800">
            <Button onClick={() => { setEditing(null); setDlgOpen(true); }} data-testid="appunti-nuovo" className="w-full bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold h-10"><Plus className="h-4 w-4 mr-1.5" /> Nuovo appunto</Button>
          </div>
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
            {loading ? (
              <div className="p-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-amber-500" /></div>
            ) : items.length === 0 ? (
              <div className="text-center text-sm py-12" style={{ color: light ? "#64748b" : "#94a3b8" }} data-testid="appunti-empty">Nessun appunto. Creane uno con "Nuovo appunto".</div>
            ) : items.map((a) => {
              const col = COLORS[a.colore] || COLORS.amber;
              const cardStyle = {
                borderLeft: `4px solid ${col.hex}`,
                border: `1px solid ${light ? "#e2e8f0" : "rgba(30,41,59,0.9)"}`,
                borderLeftColor: col.hex,
                borderLeftWidth: "4px",
                background: `linear-gradient(0deg, ${col.hex}14, ${col.hex}14), ${light ? "#ffffff" : "#0f172a"}`,
              };
              return (
                <div key={a.id} data-testid={`appunto-card-${a.id}`} className="rounded-lg p-3.5" style={cardStyle}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      {a.fissato && (
                        <span data-testid={`appunto-pin-badge-${a.id}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-semibold mb-1 px-1.5 py-0.5 rounded"
                          style={{ color: light ? "#b45309" : "#fbbf24", background: light ? "#fef3c7" : "rgba(245,158,11,0.15)" }}>
                          <Pin className="h-3 w-3 fill-current" /> Fissato
                        </span>
                      )}
                      {a.titolo && <div className="font-semibold text-sm truncate" style={{ color: light ? "#0f172a" : "#f1f5f9" }}>{a.titolo}</div>}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={() => togglePin(a)} title={a.fissato ? "Rimuovi dall'alto" : "Fissa in alto"} data-testid={`appunto-pin-${a.id}`} className={`h-7 w-7 flex items-center justify-center rounded ${a.fissato ? "text-amber-500" : "text-slate-400"} hover:bg-slate-500/15`}><Pin className={`h-4 w-4 ${a.fissato ? "fill-current" : ""}`} /></button>
                      <button onClick={() => { setEditing(a); setDlgOpen(true); }} title="Modifica" data-testid={`appunto-edit-${a.id}`} className="h-7 w-7 flex items-center justify-center rounded text-slate-400 hover:text-sky-500 hover:bg-slate-500/15"><Pencil className="h-4 w-4" /></button>
                      <button onClick={() => remove(a)} title="Elimina" data-testid={`appunto-del-${a.id}`} className="h-7 w-7 flex items-center justify-center rounded text-slate-400 hover:text-rose-500 hover:bg-slate-500/15"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                  <div className="text-sm break-words mt-1" data-testid={`appunto-contenuto-${a.id}`}
                    style={{ color: light ? "#334155" : "#cbd5e1", whiteSpace: "pre-wrap", display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                    {a.contenuto}
                  </div>
                  {a.collegato_id && (
                    <div className="inline-flex items-center gap-1.5 mt-2.5 px-2 py-1 rounded text-xs font-medium"
                      style={{ color: col.hex, background: `${col.hex}22` }}>
                      {a.collegato_tipo === "pratica" ? <FileText className="h-3.5 w-3.5" /> : <User className="h-3.5 w-3.5" />}
                      <span className="truncate max-w-[220px]">{a.collegato_label}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </SheetContent>
      </Sheet>
      <AppuntoDialog open={dlgOpen} onOpenChange={setDlgOpen} initial={editing} onSaved={load} />
    </>
  );
}
